import React, { useState, useEffect, useRef } from 'react';

const API = '/api/write';

async function api(path, body) {
  return fetch(`${API}${path}`, {
    method: body !== undefined ? 'POST' : 'GET',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addLink(href, id) {
  if (id && document.getElementById(id)) return;
  const el = Object.assign(document.createElement('link'), { rel: 'stylesheet', href });
  if (id) el.id = id;
  document.head.appendChild(el);
}

function addScript(src, onload) {
  const s = document.createElement('script');
  s.src = src;
  s.onload = onload;
  document.head.appendChild(s);
}

// Load KaTeX → MathLive → Quill in order, then call all waiting callbacks
let loaderState = 'idle'; // idle | loading | ready
let loaderQueue = [];

function loadEditor(cb) {
  if (loaderState === 'ready') { cb(); return; }
  loaderQueue.push(cb);
  if (loaderState === 'loading') return;
  loaderState = 'loading';

  addLink('https://cdn.quilljs.com/1.3.7/quill.snow.css');
  addLink('https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css');

  addScript('https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js', () => {
    addScript('https://cdn.jsdelivr.net/npm/mathlive/dist/mathlive.min.js', () => {
      addScript('https://cdn.quilljs.com/1.3.7/quill.min.js', () => {
        // Register custom iframe embed blot
        const BlockEmbed = window.Quill.import('blots/block/embed');
        class PostEmbed extends BlockEmbed {
          static create(url) {
            const node = super.create();
            node.setAttribute('data-embed', url);
            node.setAttribute('contenteditable', 'false');
            const wrap = document.createElement('div');
            wrap.className = 'post-embed-wrap';
            const iframe = document.createElement('iframe');
            iframe.src = url;
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute('frameborder', '0');
            wrap.appendChild(iframe);
            node.appendChild(wrap);
            return node;
          }
          static value(node) { return node.getAttribute('data-embed'); }
        }
        PostEmbed.blotName = 'post-embed';
        PostEmbed.tagName = 'div';
        PostEmbed.className = 'ql-post-embed';
        window.Quill.register(PostEmbed, true);

        // Raw HTML embed blot, uses srcdoc so full docs and snippets both render
        class PostHtmlEmbed extends BlockEmbed {
          static create({ html, height }) {
            const node = super.create();
            node.setAttribute('contenteditable', 'false');
            node.setAttribute('data-embed-height', height || '500');
            const wrap = document.createElement('div');
            wrap.className = 'post-embed-wrap';
            const iframe = document.createElement('iframe');
            iframe.srcdoc = html;
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
            const h = height === 'fullscreen' ? '100vh' : `${height || 500}px`;
            iframe.style.cssText = `width:100%;height:${h};border:none;display:block;`;
            wrap.appendChild(iframe);
            node.appendChild(wrap);
            return node;
          }
          static value(node) {
            const iframe = node.querySelector('iframe');
            const height = node.getAttribute('data-embed-height') || '500';
            return iframe ? { html: iframe.srcdoc, height } : { html: '', height: '500' };
          }
        }
        PostHtmlEmbed.blotName = 'post-html-embed';
        PostHtmlEmbed.tagName = 'div';
        PostHtmlEmbed.className = 'ql-post-html-embed';
        window.Quill.register(PostHtmlEmbed, true);

        loaderState = 'ready';
        loaderQueue.forEach(fn => fn());
      });
    });
  });
}

// ─── Site style injection for HTML embeds ────────────────────────────────────

function buildSiteStyleBlock() {
  const cs = getComputedStyle(document.documentElement);
  const vars = [
    '--primary-color', '--secondary-color', '--text-color',
    '--accent-color', '--accent-color-solid', '--background-color',
    '--card-background', '--border-radius', '--box-shadow', '--transition',
  ];
  const varCss = vars.map(v => `  ${v}: ${cs.getPropertyValue(v).trim()};`).join('\n');
  return `<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap">
<style>
:root {
${varCss}
}
*, *::before, *::after { box-sizing: border-box; }
body {
  margin: 0; padding: 1.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: var(--background-color);
  color: var(--text-color);
  line-height: 1.6;
}
h1,h2,h3,h4,h5,h6 { color: var(--secondary-color); }
a { color: var(--accent-color-solid); }
a:hover { color: var(--secondary-color); }
</style>`;
}

function injectSiteStyle(html) {
  const styleBlock = buildSiteStyleBlock();
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${styleBlock}\n</head>`);
  if (/<body/i.test(html)) return html.replace(/<body/i, `<head>${styleBlock}</head>\n<body`);
  return `<head>${styleBlock}</head>\n${html}`;
}

// ─── Embed URL helpers ────────────────────────────────────────────────────────

function toEmbedUrl(raw) {
  const url = raw.trim();
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
  // Vimeo
  const vi = url.match(/vimeo\.com\/(\d+)/);
  if (vi) return `https://player.vimeo.com/video/${vi[1]}`;
  // CodePen
  const cp = url.match(/codepen\.io\/([^/]+)\/pen\/([^/?#\s]+)/);
  if (cp) return `https://codepen.io/${cp[1]}/embed/${cp[2]}?default-tab=result&theme-id=dark`;
  // Twitter/X tweet
  const tw = url.match(/(?:twitter|x)\.com\/[^/]+\/status\/(\d+)/);
  if (tw) return `https://platform.twitter.com/embed/Tweet.html?id=${tw[1]}`;
  // Spotify
  if (url.includes('open.spotify.com')) return url.replace('open.spotify.com/', 'open.spotify.com/embed/');
  // Generic, use as-is
  return url;
}


// ─── Login ────────────────────────────────────────────────────────────────────

function Login({ onAuth }) {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setErr('');
    const r = await api('/login', { password });
    if (r.ok) { onAuth(); } else { setErr('Wrong password.'); setBusy(false); }
  }

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 320 }}>
        <h2 style={{ marginBottom: '1.5rem', fontWeight: 600, fontSize: '1.3rem' }}>Write</h2>
        <div className="form-group">
          <label htmlFor="wp">Password</label>
          <input id="wp" type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus style={{ width: '100%' }} />
        </div>
        {err && <p style={{ color: '#e74c3c', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>{err}</p>}
        <button type="submit" className="submit-button" disabled={busy} style={{ marginTop: '1.25rem', width: '100%' }}>
          {busy ? 'Checking…' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

// ─── Editor ───────────────────────────────────────────────────────────────────

function Editor({ onLogout }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(today());
  const [status, setStatus] = useState('');
  const [narrow, setNarrow] = useState(window.innerWidth < 800);
  const [panel, setPanel] = useState(null); // null | 'math' | 'embed'
  const [ftype, setFtype] = useState('posts'); // 'posts' | 'stories'
  // Post-level metadata
  const [pinned, setPinned] = useState(false);
  const [kind, setKind] = useState('post'); // 'post' | 'project'
  const [projImage, setProjImage] = useState('');
  const [projImageMobile, setProjImageMobile] = useState('');
  const [projEmbedUrl, setProjEmbedUrl] = useState('');
  const [projRepoUrl, setProjRepoUrl] = useState('');
  const [projTech, setProjTech] = useState('');
  const [projDateLabel, setProjDateLabel] = useState('');
  const [projFeatured, setProjFeatured] = useState(false);
  const [projFlagLabel, setProjFlagLabel] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedMode, setEmbedMode] = useState('url'); // 'url' | 'html'
  const [htmlEmbed, setHtmlEmbed] = useState('');
  const [htmlHeight, setHtmlHeight] = useState(500);
  const [htmlFullscreen, setHtmlFullscreen] = useState(false);
  const [htmlMatchStyle, setHtmlMatchStyle] = useState(false);
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const mathContainerRef = useRef(null);
  const mfRef = useRef(null);

  useEffect(() => {
    const fn = () => setNarrow(window.innerWidth < 800);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  function loadPosts(type) {
    const file = type === 'stories' ? '/stories.json' : '/posts.json';
    fetch(file, { cache: 'no-store' })
      .then(r => r.json())
      .then(d => { setPosts(d.posts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }
  useEffect(() => { setSelected(null); loadPosts(ftype); }, [ftype]);

  // Mount/unmount the MathLive math-field when the math panel opens/closes
  useEffect(() => {
    if (panel !== 'math' || !mathContainerRef.current) return;
    const mf = document.createElement('math-field');
    mf.style.cssText = 'width:100%;font-size:1.25rem;';
    mf.setAttribute('virtual-keyboard-mode', 'onfocus');
    mathContainerRef.current.appendChild(mf);
    mfRef.current = mf;
    setTimeout(() => mf.focus(), 50);
    const container = mathContainerRef.current;
    return () => { mfRef.current = null; try { container.removeChild(mf); } catch {} };
  }, [panel]);

  useEffect(() => {
    loadEditor(() => {
      if (!editorRef.current || quillRef.current) return;
      quillRef.current = new window.Quill(editorRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ header: [2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            ['link', 'blockquote', 'code-block'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['image'],
            ['clean'],
          ],
        },
      });
    });
  }, []);

  function insertMath() {
    const latex = mfRef.current?.value?.trim();
    if (!latex || !quillRef.current) return;
    const q = quillRef.current;
    const range = q.getSelection(true);
    q.insertEmbed(range.index, 'formula', latex, 'user');
    q.setSelection(range.index + 1, 0, 'user');
    setPanel(null);
  }

  function insertEmbed() {
    const url = toEmbedUrl(embedUrl.trim());
    if (!url || !quillRef.current) return;
    const q = quillRef.current;
    const range = q.getSelection(true);
    q.insertEmbed(range.index, 'post-embed', url, 'user');
    q.setSelection(range.index + 1, 0, 'user');
    setEmbedUrl('');
    setPanel(null);
  }

  function insertHtmlEmbed() {
    let html = htmlEmbed.trim();
    if (!html || !quillRef.current) return;
    if (htmlMatchStyle) html = injectSiteStyle(html);
    const q = quillRef.current;
    const range = q.getSelection(true);
    const height = htmlFullscreen ? 'fullscreen' : String(htmlHeight);
    q.insertEmbed(range.index, 'post-html-embed', { html, height }, 'user');
    q.setSelection(range.index + 1, 0, 'user');
    setHtmlEmbed('');
    setPanel(null);
  }

  function openPanel(name) {
    setPanel(p => p === name ? null : name);
    if (name === 'embed') { setEmbedUrl(''); setEmbedMode('url'); setHtmlEmbed(''); setHtmlHeight(500); setHtmlFullscreen(false); setHtmlMatchStyle(false); }
  }

  function pickPost(post) {
    setSelected(post);
    setTitle(post.title);
    setDate(post.date);
    setStatus('');
    setPinned(Boolean(post.pinned));
    setKind(post.kind === 'project' ? 'project' : 'post');
    setProjImage(post.image || '');
    setProjImageMobile(post.imageMobile || '');
    setProjEmbedUrl(post.embedUrl || '');
    setProjRepoUrl(post.repoUrl || '');
    setProjTech(Array.isArray(post.tech) ? post.tech.join(', ') : '');
    setProjDateLabel(post.dateLabel || '');
    setProjFeatured(Boolean(post.featured));
    setProjFlagLabel(post.flagLabel || '');
    if (quillRef.current) quillRef.current.root.innerHTML = post.content;
  }

  function newPost() {
    setSelected(null);
    setTitle('');
    setDate(today());
    setStatus('');
    setPinned(false);
    setKind('post');
    setProjImage('');
    setProjImageMobile('');
    setProjEmbedUrl('');
    setProjRepoUrl('');
    setProjTech('');
    setProjDateLabel('');
    setProjFeatured(false);
    setProjFlagLabel('');
    if (quillRef.current) quillRef.current.setContents([]);
  }

  async function save(publish) {
    if (!title.trim()) { setStatus('Title is required.'); return; }
    const content = quillRef.current?.root.innerHTML ?? '';
    const text = quillRef.current?.getText().trim() ?? '';
    const excerpt = text.slice(0, 220) + (text.length > 220 ? '…' : '');
    const id = selected?.id ?? crypto.randomUUID();
    const entry = { id, ftype, title: title.trim(), content, excerpt, date, published: publish, pinned, kind };
    if (kind === 'project') {
      Object.assign(entry, {
        image: projImage.trim(),
        imageMobile: projImageMobile.trim(),
        embedUrl: projEmbedUrl.trim(),
        repoUrl: projRepoUrl.trim(),
        dateLabel: projDateLabel.trim(),
        featured: projFeatured,
        flagLabel: projFlagLabel.trim(),
        tech: projTech.split(',').map(t => t.trim()).filter(Boolean),
      });
    }
    setStatus(publish ? 'Publishing…' : 'Saving draft…');
    const r = await api('/save', entry);
    if (r.ok) {
      setSelected(entry); loadPosts(ftype);
      setStatus(publish ? 'Published.' : 'Draft saved.');
    } else if (r.status === 401) {
      onLogout();
    } else {
      setStatus('Save failed.');
    }
  }

  async function del() {
    if (!selected || !window.confirm('Delete this post?')) return;
    setStatus('Deleting…');
    const r = await api('/delete', { id: selected.id, ftype });
    if (r.ok) { loadPosts(ftype); newPost(); setStatus('Deleted.'); }
  }

  async function logout() {
    await api('/logout', {});
    onLogout();
  }

  const sorted = [...posts].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="container" style={{ paddingTop: 'calc(72px + 2rem)', paddingBottom: '5rem' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h3 className="section-title" style={{ margin: 0 }}>Write</h3>
        <button onClick={logout} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.45, fontSize: '0.85rem', color: 'inherit', fontFamily: 'inherit' }}>
          Sign out
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '210px 1fr', gap: '2rem', alignItems: 'start' }}>

        {/* Sidebar */}
        <div>
          <button className="submit-button" onClick={newPost} style={{ width: '100%', marginBottom: '0.9rem' }}>
            + New post
          </button>
          {loading && <p style={{ opacity: 0.45, fontSize: '0.85rem' }}>Loading…</p>}
          {sorted.map(p => (
            <div
              key={p.id}
              onClick={() => pickPost(p)}
              className={`write-sidebar-item${selected?.id === p.id ? ' active' : ''}`}
            >
              <div style={{ fontSize: '0.87rem', fontWeight: 500, lineHeight: 1.35 }}>{p.title || '(untitled)'}</div>
              <div style={{ fontSize: '0.72rem', opacity: 0.45, marginTop: 3 }}>
                {p.date}{!p.published ? ' · draft' : ''}{p.kind === 'project' ? ' · project' : ''}{p.pinned ? ' · 📌' : ''}
              </div>
            </div>
          ))}
        </div>

        {/* Editor */}
        <div>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" className="write-title-input" />
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="write-date-input" />
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <button className={ftype === 'posts' ? 'submit-button' : 'write-btn-secondary'} onClick={() => setFtype('posts')} style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>Technical</button>
            <button className={ftype === 'stories' ? 'submit-button' : 'write-btn-secondary'} onClick={() => setFtype('stories')} style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>Story</button>
          </div>

          {/* Post kind + pin to homepage */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className={kind === 'post' ? 'submit-button' : 'write-btn-secondary'}
              onClick={() => setKind('post')}
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
            >Post</button>
            <button
              className={kind === 'project' ? 'submit-button' : 'write-btn-secondary'}
              onClick={() => setKind('project')}
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
            >Project</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', opacity: 0.8, marginLeft: '0.5rem' }}>
              <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
              📌 Pin to homepage
            </label>
          </div>

          {/* Project card fields */}
          {kind === 'project' && (
            <div className="write-inline-panel" style={{ marginBottom: '0.75rem' }}>
              <p style={{ opacity: 0.55, fontSize: '0.82rem', marginBottom: '0.9rem' }}>
                Project card fields, these render on both the Posts page and the homepage.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', opacity: 0.8, marginBottom: '0.8rem' }}>
                <input type="checkbox" checked={projFeatured} onChange={e => setProjFeatured(e.target.checked)} />
                Feature this project (full width, larger schematic)
              </label>
              {[
                { label: 'Image URL', value: projImage, set: setProjImage, ph: '/images/example.png' },
                { label: 'Schematic / iframe embed URL (desktop only)', value: projEmbedUrl, set: setProjEmbedUrl, ph: 'https://kicanvas.org/?github=...' },
                { label: 'Mobile fallback image (used when embed is set)', value: projImageMobile, set: setProjImageMobile, ph: '/images/example.png' },
                { label: 'Repo URL', value: projRepoUrl, set: setProjRepoUrl, ph: 'https://github.com/...' },
                { label: 'Tech tags (comma separated)', value: projTech, set: setProjTech, ph: 'C++, ESP32-S3, KiCad' },
                { label: 'Date label (optional, shown on card)', value: projDateLabel, set: setProjDateLabel, ph: 'In progress' },
                { label: 'Feature badge text (optional)', value: projFlagLabel, set: setProjFlagLabel, ph: 'Flagship build' },
              ].map(f => (
                <div key={f.label} style={{ marginBottom: '0.7rem' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', opacity: 0.7, marginBottom: '0.25rem' }}>{f.label}</label>
                  <input
                    type="text"
                    value={f.value}
                    onChange={e => f.set(e.target.value)}
                    placeholder={f.ph}
                    style={{
                      width: '100%', padding: '0.4rem 0.6rem', fontSize: '0.85rem',
                      background: 'transparent', color: 'inherit', fontFamily: 'inherit',
                      border: '1px solid rgba(128,128,128,0.3)', borderRadius: 'var(--border-radius)',
                    }}
                  />
                </div>
              ))}
              <p style={{ opacity: 0.45, fontSize: '0.75rem', margin: '0.5rem 0 0' }}>
                The editor body below becomes the card description.
              </p>
            </div>
          )}

          {/* Insert buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <button
              className={`write-btn-secondary${panel === 'math' ? ' active' : ''}`}
              onClick={() => openPanel('math')}
              style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem' }}
            >
              ∑ Math
            </button>
            <button
              className={`write-btn-secondary${panel === 'embed' ? ' active' : ''}`}
              onClick={() => openPanel('embed')}
              style={{ fontSize: '0.82rem', padding: '0.3rem 0.8rem' }}
            >
              ⊞ Embed
            </button>
          </div>

          {/* Inline math panel */}
          {panel === 'math' && (
            <div className="write-inline-panel">
              <p style={{ opacity: 0.55, fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                Type LaTeX or use the interactive keyboard below.
              </p>
              <div ref={mathContainerRef} />
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem' }}>
                <button className="submit-button" onClick={insertMath}>Insert</button>
                <button className="write-btn-secondary" onClick={() => setPanel(null)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Inline embed panel */}
          {panel === 'embed' && (
            <div className="write-inline-panel">
              <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem' }}>
                <button
                  className={embedMode === 'url' ? 'submit-button' : 'write-btn-secondary'}
                  onClick={() => setEmbedMode('url')}
                  style={{ fontSize: '0.78rem', padding: '0.2rem 0.65rem' }}
                >URL</button>
                <button
                  className={embedMode === 'html' ? 'submit-button' : 'write-btn-secondary'}
                  onClick={() => setEmbedMode('html')}
                  style={{ fontSize: '0.78rem', padding: '0.2rem 0.65rem' }}
                >HTML</button>
              </div>

              {embedMode === 'url' && <>
                <p style={{ opacity: 0.55, fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                  YouTube, Vimeo, CodePen, Twitter/X, Spotify, or any iframe URL.
                </p>
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <input
                    type="text"
                    value={embedUrl}
                    onChange={e => setEmbedUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && insertEmbed()}
                    placeholder="https://..."
                    autoFocus
                    style={{ width: '100%' }}
                  />
                </div>
                {embedUrl.trim() && (
                  <div style={{ borderRadius: '8px', overflow: 'hidden', aspectRatio: '16/9', marginBottom: '0.75rem', background: 'rgba(0,0,0,0.2)' }}>
                    <iframe src={toEmbedUrl(embedUrl)} title="preview" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <button className="submit-button" onClick={insertEmbed} disabled={!embedUrl.trim()}>Insert</button>
                  <button className="write-btn-secondary" onClick={() => setPanel(null)}>Cancel</button>
                </div>
              </>}

              {embedMode === 'html' && <>
                <p style={{ opacity: 0.55, fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                  Paste any HTML, snippets, full documents, embed codes.
                </p>
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <textarea
                    value={htmlEmbed}
                    onChange={e => setHtmlEmbed(e.target.value)}
                    placeholder="<iframe src=...></iframe>"
                    autoFocus
                    rows={5}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.82rem', resize: 'vertical' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', opacity: 0.75 }}>
                    <input type="checkbox" checked={htmlFullscreen} onChange={e => setHtmlFullscreen(e.target.checked)} />
                    Full page height
                  </label>
                  {!htmlFullscreen && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', opacity: 0.75 }}>
                      Height
                      <input
                        type="number" min={100} max={4000} step={50} value={htmlHeight}
                        onChange={e => setHtmlHeight(Number(e.target.value))}
                        style={{ width: '70px', padding: '0.2rem 0.4rem', fontSize: '0.82rem' }}
                      />
                      px
                    </label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', opacity: 0.75 }}>
                    <input type="checkbox" checked={htmlMatchStyle} onChange={e => setHtmlMatchStyle(e.target.checked)} />
                    Match site style
                  </label>
                </div>
                {htmlEmbed.trim() && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <p style={{ opacity: 0.45, fontSize: '0.75rem', marginBottom: '0.4rem' }}>Preview</p>
                    <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <iframe
                        srcDoc={htmlMatchStyle ? injectSiteStyle(htmlEmbed) : htmlEmbed}
                        title="html-preview"
                        sandbox="allow-scripts allow-same-origin"
                        style={{
                          width: '100%',
                          height: htmlFullscreen ? '60vh' : `${Math.min(htmlHeight, 500)}px`,
                          border: 'none',
                          display: 'block',
                        }}
                      />
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <button className="submit-button" onClick={insertHtmlEmbed} disabled={!htmlEmbed.trim()}>Insert</button>
                  <button className="write-btn-secondary" onClick={() => setPanel(null)}>Cancel</button>
                </div>
              </>}
            </div>
          )}

          <div ref={editorRef} style={{ minHeight: 320 }} />

          <div style={{ display: 'flex', gap: '0.7rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <button className="submit-button" onClick={() => save(true)}>Publish</button>
            <button className="write-btn-secondary" onClick={() => save(false)}>Save draft</button>
            {selected && <button className="write-btn-danger" onClick={del}>Delete</button>}
          </div>
          {status && <p style={{ marginTop: '0.75rem', fontSize: '0.88rem', opacity: 0.65 }}>{status}</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function Write() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    api('/check').then(r => r.json()).then(d => setAuthed(d.authed)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) return null;
  if (!authed) return <Login onAuth={() => setAuthed(true)} />;
  return <Editor onLogout={() => setAuthed(false)} />;
}

